/**
 * Admin panel: Audit Log viewer (SSO sign-ins/failures, bulk actions, status changes,
 * site section toggles, branding changes and all admin write requests).
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { ScrollText, Loader2, RefreshCw, ChevronLeft, ChevronRight, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { API_BASE } from '../../config/api';

const API = `${API_BASE}/api`;
const PAGE = 50;

export default function AuditLogTab({ token }) {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [actions, setActions] = useState([]);
  const [action, setAction] = useState('');
  const [search, setSearch] = useState('');
  const [outcome, setOutcome] = useState('');
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE, skip };
      if (action) params.action = action;
      if (search) params.search = search;
      if (outcome) params.success = outcome === 'success';
      const res = await axios.get(`${API}/admin/audit-logs`, { headers: { Authorization: `Bearer ${token}` }, params });
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
      setActions(res.data.actions || []);
    } catch (e) {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [token, action, search, outcome, skip]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5" data-testid="audit-log-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold text-white flex items-center gap-2"><ScrollText className="h-6 w-6 text-navigate-gold" /> Audit Log</h2>
          <p className="text-sm text-zinc-400">{total} entries</p>
        </div>
        <Button variant="outline" className="border-zinc-700 text-zinc-300" onClick={load} data-testid="audit-refresh"><RefreshCw className="h-4 w-4" /> Refresh</Button>
      </div>
      <div className="flex flex-wrap gap-3">
        <select value={action} onChange={(e) => { setSkip(0); setAction(e.target.value); }} className="h-10 rounded-md bg-zinc-900 border border-zinc-700 text-white px-2 text-sm" data-testid="audit-action-filter">
          <option value="">All actions</option>
          {actions.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <select value={outcome} onChange={(e) => { setSkip(0); setOutcome(e.target.value); }} className="h-10 rounded-md bg-zinc-900 border border-zinc-700 text-white px-2 text-sm" data-testid="audit-outcome-filter">
          <option value="">Any outcome</option>
          <option value="success">Success</option>
          <option value="failure">Failure</option>
        </select>
        <Input value={search} onChange={(e) => { setSkip(0); setSearch(e.target.value); }} placeholder="Search email, target or action" className="w-72 bg-zinc-900 border-zinc-700 text-white" data-testid="audit-search" />
      </div>
      <div className="rounded-xl border border-zinc-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-navigate-surface text-zinc-400 text-xs uppercase">
            <tr><th className="text-left p-3">When</th><th className="text-left p-3">Action</th><th className="text-left p-3">Who</th><th className="text-left p-3">Target</th><th className="text-left p-3">Outcome</th></tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="p-8 text-center"><Loader2 className="h-6 w-6 animate-spin text-navigate-gold inline" /></td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={5} className="p-8 text-center text-zinc-500">No audit entries</td></tr>
            ) : logs.map((l) => (
              <React.Fragment key={l.log_id}>
                <tr className="border-t border-zinc-800 hover:bg-zinc-800/30 cursor-pointer" onClick={() => setExpanded(expanded === l.log_id ? null : l.log_id)} data-testid="audit-row">
                  <td className="p-3 text-zinc-400 whitespace-nowrap">{l.timestamp ? new Date(l.timestamp).toLocaleString() : '-'}</td>
                  <td className="p-3 text-white font-mono text-xs">{l.action}</td>
                  <td className="p-3 text-zinc-300">{l.admin_email || '-'}</td>
                  <td className="p-3 text-zinc-400 max-w-xs truncate">{l.target_type}{l.target_id ? ` · ${l.target_id}` : ''}</td>
                  <td className="p-3">{l.success === false ? <span className="inline-flex items-center gap-1 text-red-400"><XCircle className="h-4 w-4" />Failed</span> : <span className="inline-flex items-center gap-1 text-emerald-400"><CheckCircle2 className="h-4 w-4" />OK</span>}</td>
                </tr>
                {expanded === l.log_id && (
                  <tr className="bg-zinc-900/60"><td colSpan={5} className="p-3"><pre className="text-xs text-zinc-300 whitespace-pre-wrap break-all">{JSON.stringify({ details: l.details, ip: l.ip_address, user_agent: l.user_agent, org_id: l.org_id }, null, 2)}</pre></td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" size="sm" className="border-zinc-700 text-zinc-300" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE))}><ChevronLeft className="h-4 w-4" /></Button>
        <span className="text-xs text-zinc-500">{total ? `${skip + 1}-${Math.min(skip + PAGE, total)} of ${total}` : '0'}</span>
        <Button variant="outline" size="sm" className="border-zinc-700 text-zinc-300" disabled={skip + PAGE >= total} onClick={() => setSkip(skip + PAGE)}><ChevronRight className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}
